import React from 'react';
import ReactDOM from 'react-dom';
import { MessageBoxEl, resetBodyScrollLock } from './messageBox';
import { DEFAULT_PROP } from './const';

const MessageBoxInstance = {
  // 目的是为了存储多个实例弹窗，如果为空就建一个新的实例
  boxs: [] as any,
  // 获取一个实例
  getInstance() {
    const boxs = this.boxs;
    // 当只有一个实例，并且该实例还是隐藏的时候就取出来，否则都是去新建新的实例
    if (boxs.length === 1 && boxs[0].isShowing === false) {
      return boxs[0];
    }
    return {};
  },
  // 创建一个新的实例
  setInstance(instance, domEl) {
    this.boxs.push({
      isShowing: true,
      domEl,
      instance,
    });
    return this.boxs.length - 1;
  },
  // 创建之后就需要移除才行
  removeInstance(index) {
    // 保留一个实例，这样就不需要经常创建新的节点了
    if (this.boxs.length === 1) {
      this.boxs[0].isShowing = false;
      return;
    }
    const box = this.boxs[index];
    document.body.removeChild(box.domEl);
    this.boxs.splice(index, 1);
  },
  // 按实例引用移除（并发安全）：不依赖下标，避免多个弹窗并发关闭时
  // splice 导致后续实例下标前移、按旧下标移除到错误实例或越界读 undefined.domEl 崩溃
  removeInstanceRef(box) {
    if (!box) return;
    // 只剩这一个实例：保留复用（仅隐藏，不销毁 DOM 节点）
    if (this.boxs.length === 1 && this.boxs[0] === box) {
      box.isShowing = false;
      return;
    }
    const index = this.boxs.indexOf(box);
    if (index < 0) return; // 已被移除，防重复移除
    if (box.domEl && box.domEl.parentNode) {
      box.domEl.parentNode.removeChild(box.domEl);
    }
    this.boxs.splice(index, 1);
  },
  // 移除所有的弹窗
  removeAll() {
    // 遍历快照 + 按引用移除：避免在 forEach 中 splice 原数组导致下标前移、
    // 跳过后续并发弹窗（原实现两个实例时会漏关第二个）
    const snapshot = [...this.boxs];
    snapshot.forEach((box) => {
      if (!box) return;
      box.instance.close(() => {
        // 走引用计数解锁，避免直接清空 overflow 破坏其它仍打开弹窗的锁状态
        box.instance.unlockScrollIfNeeded();
      });
      // 按引用移除，不依赖下标，逐实例关闭/解锁
      this.removeInstanceRef(box);
    });
    // 兜底：强制复位全局滚动锁，保证 removeAll 后页面一定可滚动
    resetBodyScrollLock();
  },
  render(options) {
    let { instance } = this.getInstance();
    const that = this;
    if (options.type === 'custom') {
      if (instance) {
        // 如果找到未使用的实例，那就直接更新实例的参数即可
        instance.update(options);
      } else {
        // 如果没有在弹窗实例数组中找到，那就创建一个
        const domEl = document.createElement('div');
        document.body.appendChild(domEl);
        instance = ReactDOM.render(
          <MessageBoxEl
            ref={t => {
              instance = t;
            }}
            {...options}
          />,
          domEl
        );
      }
      return instance;
    }
    return new Promise((resolve, reject) => {
      let currentBox;
      const callback = (action, data) => {
        // 按引用移除，避免多弹窗并发关闭时的下标错位/越界崩溃
        that.removeInstanceRef(currentBox);
        if (action === 'confirm') {
          resolve(data);
        } else {
          reject(data);
        }
      };
      options.callback = callback;
      if (instance) {
        // 如果找到未使用的实例，那就直接更新实例的参数即可
        instance.update(options);
        currentBox = this.boxs[0];
      } else {
        // 如果没有在弹窗实例数组中找到，那就创建一个
        const domEl = document.createElement('div');
        document.body.appendChild(domEl);
        instance = ReactDOM.render(
          <MessageBoxEl
            ref={t => {
              instance = t;
            }}
            {...options}
          />,
          domEl
        );
        // 存储实例并记住其引用（不依赖下标，避免并发 splice 后错位）
        this.setInstance(instance, domEl);
        currentBox = this.boxs[this.boxs.length - 1];
      }
    });
  }
};

// 该组件是用于JS直接调用形式
export const MessageBox = {
  // alert 弹窗
  alert: (message, options = {}) => {
    if (!React.isValidElement(message) && typeof message === 'object') {
      options = message;
    }
    return MessageBoxInstance.render(
      Object.assign(
        {},
        DEFAULT_PROP,
        {
          message,
          type: 'alert',
          showCancelButton: true,
          show: true
        },
        options
      )
    );
  },
  // confirm 弹窗
  confirm: (message, options = {}) => {
    if (!React.isValidElement(message) && typeof message === 'object') {
      options = message;
    }
    return MessageBoxInstance.render(
      Object.assign(
        {},
        DEFAULT_PROP,
        {
          message,
          type: 'confirm',
          showCancelButton: true,
          show: true
        },
        options
      )
    );
  },
  // toast 弹窗
  toast: (message, options = {}) => {
    if (!React.isValidElement(message) && typeof message === 'object') {
      options = message;
    }
    return MessageBoxInstance.render(
      Object.assign(
        {},
        DEFAULT_PROP,
        {
          message,
          timeout: 2000,
          opacity: 'transparent',
          type: 'toast',
          show: true
        },
        options
      )
    );
  },
  // 自定义弹窗
  custom: (message, options = {}) => {
    if (
      !React.isValidElement(message) &&
      typeof message !== 'function' &&
      typeof message === 'object'
    ) {
      options = message;
    }
    return MessageBoxInstance.render(
      Object.assign(
        {},
        DEFAULT_PROP,
        {
          message,
          type: 'custom',
          show: true
        },
        options
      )
    );
  },
  removeAll() {
    return MessageBoxInstance.removeAll();
  }
};

export default MessageBox;

export { MessageBoxEl };
